package no.vareflow.app.report

import no.vareflow.app.domain.formatDateTime

import no.vareflow.app.domain.AppLanguage
import no.vareflow.app.domain.compareNorwegian
import no.vareflow.app.domain.tr

/**
 * Lays out a stock-count report (same result for the phone and the web): groups by Hovedvaregruppe,
 * leverandør or both, on one sheet (summary at the end) or one sheet per group (summary on its own
 * sheet, first). The writers (XLSX, PDF) only draw what this produces.
 */
object ReportBuilder {

    fun build(request: ReportRequest): LaidOutReport {
        val lang = request.language
        fun l(nb: String, en: String) = tr(lang, nb, en)
        fun shown(name: String) = reportGroupName(name, lang)
        val lines = request.lines.map {
            it.copy(
                category = it.category.ifBlank { UNKNOWN_CATEGORY },
                supplier = it.supplier.trim().ifBlank { NO_SUPPLIER }
            )
        }.sortedWith { a, b -> compareNorwegian(a.name, b.name) }

        val categoryComparator = categoryComparator(request.categoryOrder)
        val supplierComparator = Comparator<String> { a, b ->
            when {
                a == b -> 0
                a == NO_SUPPLIER -> 1
                b == NO_SUPPLIER -> -1
                else -> compareNorwegian(a, b)
            }
        }
        val topBySupplier = request.grouping == ReportGrouping.SUPPLIER
        val undergruppeComparator = Comparator<String> { a, b ->
            val ia = request.undergruppeOrder.indexOfFirst { it.equals(a, true) }.let { if (it < 0) Int.MAX_VALUE else it }
            val ib = request.undergruppeOrder.indexOfFirst { it.equals(b, true) }.let { if (it < 0) Int.MAX_VALUE else it }
            when {
                a == b -> 0
                a.isEmpty() -> -1
                b.isEmpty() -> 1
                ia != ib -> ia.compareTo(ib)
                else -> compareNorwegian(a, b)
            }
        }
        /** Lines without an undergruppe first (key ""), then each undergruppe in the app's order. */
        fun undergroupsOf(group: List<ReportLine>): List<Pair<String, List<ReportLine>>> =
            group.groupBy { it.undergruppe.trim() }.toList().sortedWith { a, b -> undergruppeComparator.compare(a.first, b.first) }
        val topGroups: List<Pair<String, List<ReportLine>>> =
            if (topBySupplier) lines.groupBy { it.supplier }.toList().sortedWith { a, b -> supplierComparator.compare(a.first, b.first) }
            else lines.groupBy { it.category }.toList().sortedWith { a, b -> categoryComparator.compare(a.first, b.first) }

        val detail = detailColumns(request.grouping, request.showPrices, lang)
        fun item(line: ReportLine): ReportRow.Item = ReportRow.Item(detail.map { column ->
            when (column.key) {
                COL_NUMBER -> line.varenummer
                COL_PRODUCT -> line.name
                COL_SUPPLIER -> shown(line.supplier)
                COL_CATEGORY -> shown(line.category)
                COL_UNIT -> line.unit
                COL_QUANTITY -> line.quantity
                COL_PRICE -> line.pricePerUnit
                COL_VALUE -> line.value
                else -> ""
            }
        })
        fun sum(label: String, group: List<ReportLine>, strong: Boolean): ReportRow.Sum {
            val text = if (request.showPrices) label else "$label (${group.size} ${l("produkter", "products")})"
            return ReportRow.Sum(detail.mapIndexed { i, column ->
                when {
                    i == 0 -> text
                    column.key == COL_VALUE -> group.sumOf { it.value }
                    else -> null
                }
            }, strong)
        }
        fun groupRows(title: String, group: List<ReportLine>): List<ReportRow> = buildList {
            add(ReportRow.Heading(shown(title)))
            add(ReportRow.Header(detail))
            if (request.grouping == ReportGrouping.BOTH) {
                group.groupBy { it.supplier }.toList().sortedWith { a, b -> supplierComparator.compare(a.first, b.first) }
                    .forEach { (supplier, sub) ->
                        add(ReportRow.SubHeading(shown(supplier)))
                        sub.forEach { add(item(it)) }
                        add(sum("Sum ${shown(supplier)}", sub, strong = false))
                    }
            } else if (!topBySupplier && group.any { it.undergruppe.isNotBlank() }) {
                // Hovedvare divided into undergrupper (e.g. Vin → Rødvin, Hvitvin): a sum for each.
                undergroupsOf(group).forEach { (undergruppe, sub) ->
                    if (undergruppe.isEmpty()) sub.forEach { add(item(it)) }
                    else {
                        add(ReportRow.SubHeading(undergruppe))
                        sub.forEach { add(item(it)) }
                        add(sum("Sum $undergruppe", sub, strong = false))
                    }
                }
            } else {
                group.forEach { add(item(it)) }
            }
            add(sum("Sum ${shown(title)}", group, strong = true))
        }

        val summary = summaryRows(lines, request, categoryComparator, supplierComparator, ::undergroupsOf)
        val sheets = when (request.layout) {
            ReportLayout.SINGLE -> {
                val rows = buildList {
                    topGroups.forEach { (title, group) -> addAll(groupRows(title, group)); add(ReportRow.Blank) }
                    if (topGroups.isEmpty()) add(ReportRow.Heading(l("Ingen produkter er telt", "No products have been counted")))
                    addAll(summary)
                }
                listOf(ReportSheet(l("Varetelling", "Stock count"), detail, rows))
            }
            ReportLayout.PER_GROUP -> {
                val used = mutableSetOf<String>()
                listOf(ReportSheet(uniqueSheetName(l("Oppsummering", "Summary"), used), summaryColumns(l("Gruppe", "Group"), request.showPrices, lang), summary)) +
                    topGroups.map { (title, group) -> ReportSheet(uniqueSheetName(shown(title), used), detail, groupRows(title, group)) }
            }
        }
        val withSvinn = if (request.svinn.isEmpty()) sheets else sheets + svinnSheet(request, categoryComparator, sheets.map { it.name }.toMutableSet())
        return LaidOutReport(request.title, request.subtitle, withSvinn, lang, request.companyName.trim(), JpegInfo.read(request.logoJpeg))
    }

    /** Svinn: every registration, then the sum per årsak and per hovedvaregruppe. */
    private fun svinnSheet(request: ReportRequest, categoryComparator: Comparator<String>, used: MutableSet<String>): ReportSheet {
        val lang = request.language
        fun l(nb: String, en: String) = tr(lang, nb, en)
        val records = request.svinn.sortedWith(compareBy<no.vareflow.app.domain.SvinnRecord, String>(categoryComparator) { it.category }.thenBy { it.createdAt })
        val columns = buildList {
            add(ReportColumn(l("Dato", "Date"), CellKind.TEXT, 16, "date"))
            add(ReportColumn(l("Produkt", "Product"), CellKind.TEXT, 30, COL_PRODUCT))
            add(ReportColumn(l("Hovedvaregruppe", "Main category"), CellKind.TEXT, 16, COL_CATEGORY))
            add(ReportColumn(l("Årsak", "Reason"), CellKind.TEXT, 11, "reason"))
            add(ReportColumn(l("Antall", "Quantity"), CellKind.QUANTITY, 9, COL_QUANTITY))
            add(ReportColumn(l("Enhet", "Unit"), CellKind.TEXT, 7, COL_UNIT))
            if (request.showPrices) add(ReportColumn(l("Verdi", "Value"), CellKind.MONEY, 12, COL_VALUE))
            add(ReportColumn(l("Registrert av", "Registered by"), CellKind.TEXT, 16, "user"))
            add(ReportColumn(l("Kommentar", "Comment"), CellKind.TEXT, 22, "note"))
        }
        val rows = buildList {
            records.forEach { r ->
                add(ReportRow.Item(buildList {
                    add(formatDateTime(r.createdAt)); add(r.productName); add(reportGroupName(r.category, lang)); add(r.reason.labelIn(lang))
                    add(r.quantity); add(r.unit); if (request.showPrices) add(r.value); add(r.userName); add(r.note)
                }))
            }
            add(ReportRow.Sum(buildList {
                add(l("Totalt", "Total")); add(null); add(null); add(null); add(null); add(null)
                if (request.showPrices) add(records.sumOf { it.value }); add(null); add(null)
            }, strong = true))
            val summaryColumns = buildList {
                add(ReportColumn(l("Gruppe", "Group"), CellKind.TEXT, 36, "group"))
                add(ReportColumn(l("Registreringer", "Registrations"), CellKind.COUNT, 12, "count"))
                if (request.showPrices) add(ReportColumn(l("Verdi", "Value"), CellKind.MONEY, 16, COL_VALUE))
            }
            fun table(title: String, groups: List<Pair<String, List<no.vareflow.app.domain.SvinnRecord>>>) {
                add(ReportRow.Blank)
                add(ReportRow.Heading(title))
                add(ReportRow.Header(summaryColumns))
                groups.forEach { (name, group) -> add(ReportRow.Item(buildList { add(name); add(group.size); if (request.showPrices) add(group.sumOf { it.value }) })) }
            }
            table(l("Svinn per årsak", "Svinn per reason"), records.groupBy { it.reason }.toList().sortedBy { it.first.ordinal }.map { (reason, g) -> reason.labelIn(lang) to g })
            table(l("Svinn per hovedvaregruppe", "Svinn per main category"), records.groupBy { it.category }.toList()
                .sortedWith { a, b -> categoryComparator.compare(a.first, b.first) }.map { (c, g) -> reportGroupName(c, lang) to g })
        }
        return ReportSheet(uniqueSheetName(l("Svinn", "Svinn"), used), columns, rows)
    }

    private fun categoryComparator(order: List<String>) = Comparator<String> { a, b ->
        val ia = order.indexOf(a).let { if (it < 0) Int.MAX_VALUE else it }
        val ib = order.indexOf(b).let { if (it < 0) Int.MAX_VALUE else it }
        if (ia != ib) ia.compareTo(ib) else compareNorwegian(a, b)
    }

    private const val COL_NUMBER = "number"
    private const val COL_PRODUCT = "product"
    private const val COL_SUPPLIER = "supplier"
    private const val COL_CATEGORY = "category"
    private const val COL_UNIT = "unit"
    private const val COL_QUANTITY = "quantity"
    private const val COL_PRICE = "price"
    private const val COL_VALUE = "value"

    fun detailColumns(grouping: ReportGrouping, showPrices: Boolean, language: AppLanguage = AppLanguage.NB): List<ReportColumn> = buildList {
        fun l(nb: String, en: String) = tr(language, nb, en)
        add(ReportColumn(l("Varenr.", "Item no."), CellKind.TEXT, 11, COL_NUMBER))
        add(ReportColumn(l("Produkt", "Product"), CellKind.TEXT, 34, COL_PRODUCT))
        when (grouping) {
            ReportGrouping.CATEGORY -> add(ReportColumn(l("Leverandør", "Supplier"), CellKind.TEXT, 18, COL_SUPPLIER))
            ReportGrouping.SUPPLIER -> add(ReportColumn(l("Hovedvaregruppe", "Main category"), CellKind.TEXT, 18, COL_CATEGORY))
            ReportGrouping.BOTH -> Unit
        }
        add(ReportColumn(l("Enhet", "Unit"), CellKind.TEXT, 8, COL_UNIT))
        add(ReportColumn(l("Antall", "Quantity"), CellKind.QUANTITY, 10, COL_QUANTITY))
        if (showPrices) {
            add(ReportColumn(l("Pris", "Price"), CellKind.MONEY, 12, COL_PRICE))
            add(ReportColumn(l("Verdi", "Value"), CellKind.MONEY, 14, COL_VALUE))
        }
    }

    private fun summaryColumns(first: String, showPrices: Boolean, language: AppLanguage): List<ReportColumn> = buildList {
        add(ReportColumn(first, CellKind.TEXT, 36, "group"))
        add(ReportColumn(tr(language, "Produkter", "Products"), CellKind.COUNT, 12, "count"))
        if (showPrices) add(ReportColumn(tr(language, "Verdi", "Value"), CellKind.MONEY, 16, COL_VALUE))
    }

    /** Summary per Hovedvaregruppe (always) and per leverandør (when grouped by leverandør). */
    private fun summaryRows(
        lines: List<ReportLine>,
        request: ReportRequest,
        categoryComparator: Comparator<String>,
        supplierComparator: Comparator<String>,
        undergroupsOf: (List<ReportLine>) -> List<Pair<String, List<ReportLine>>>
    ): List<ReportRow> = buildList {
        val lang = request.language
        fun l(nb: String, en: String) = tr(lang, nb, en)
        fun table(first: String, groups: List<Pair<String, List<ReportLine>>>, withUndergrupper: Boolean = false) {
            val columns = summaryColumns(first, request.showPrices, lang)
            add(ReportRow.Header(columns))
            groups.forEach { (name, group) ->
                add(ReportRow.Item(buildList { add(reportGroupName(name, lang)); add(group.size); if (request.showPrices) add(group.sumOf { it.value }) }))
                // Vin → "   Rødvin", "   Hvitvin" … indented under their hovedvare.
                if (withUndergrupper && group.any { it.undergruppe.isNotBlank() }) undergroupsOf(group).forEach { (ug, sub) ->
                    val label = "    " + ug.ifEmpty { l("Uten undergruppe", "No subgroup") }
                    add(ReportRow.Item(buildList { add(label); add(sub.size); if (request.showPrices) add(sub.sumOf { it.value }) }))
                }
            }
            add(ReportRow.Sum(buildList { add(l("Totalt", "Total")); add(lines.size); if (request.showPrices) add(lines.sumOf { it.value }) }, strong = true))
        }
        add(ReportRow.Heading(l("Oppsummering per hovedvaregruppe", "Summary per main category")))
        table(l("Hovedvaregruppe", "Main category"), lines.groupBy { it.category }.toList().sortedWith { a, b -> categoryComparator.compare(a.first, b.first) }, withUndergrupper = true)
        if (request.grouping != ReportGrouping.CATEGORY) {
            add(ReportRow.Blank)
            add(ReportRow.Heading(l("Oppsummering per leverandør", "Summary per supplier")))
            table(l("Leverandør", "Supplier"), lines.groupBy { it.supplier }.toList().sortedWith { a, b -> supplierComparator.compare(a.first, b.first) })
        }
    }

    /** Excel sheet names: no []:*?/\, at most 31 characters, unique. */
    fun uniqueSheetName(raw: String, used: MutableSet<String>): String {
        val clean = raw.replace(Regex("[\\[\\]:*?/\\\\]"), " ").trim().ifBlank { "Ark" }.take(31)
        var name = clean
        var n = 2
        while (name.lowercase() in used) {
            val suffix = " ($n)"
            name = clean.take(31 - suffix.length) + suffix
            n++
        }
        used += name.lowercase()
        return name
    }

    /** "Varetelling_2026-10_Honefoss.xlsx": ASCII only (some browsers and mail apps drop other names). */
    fun fileName(yearMonth: String, location: String, format: ReportFormat, language: AppLanguage = AppLanguage.NB): String {
        val ascii = location.replace("æ", "ae").replace("Æ", "Ae").replace("ø", "o").replace("Ø", "O").replace("å", "a").replace("Å", "A")
        val place = ascii.replace(Regex("[^A-Za-z0-9_-]+"), "_").trim('_').ifBlank { "Vareflow" }
        val prefix = if (language == AppLanguage.EN) "Stock_count" else "Varetelling"
        return "${prefix}_${yearMonth}_$place.${format.extension}"
    }
}
